import React from "react";

type LiveTruckIndicatorProps = {
  /** Overall height of the pill in px. Everything scales from this. */
  size?: number;
  /** Seconds per road-dash cycle. Lower = faster. */
  speed?: number;
  /** Pause the road when nothing is in transit. */
  paused?: boolean;
  /** Accent colour. Truck, text and dot derive from this. */
  color?: string;
  className?: string;
  /** Hide from assistive tech when a parent link/button provides the label. */
  ariaHidden?: boolean;
  /** Visible label. */
  label?: string;
};

const CSS = `
@keyframes ec-road-scroll {
  from { stroke-dashoffset: 0; }
  to   { stroke-dashoffset: -14; }
}
@keyframes ec-live-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.35; transform: scale(0.75); }
}
.ec-live-road { animation: ec-road-scroll var(--ec-live-speed, 0.7s) linear infinite; }
.ec-live-dot  { animation: ec-live-pulse 1.4s ease-in-out infinite; transform-origin: center; }
.ec-live-paused .ec-live-road,
.ec-live-paused .ec-live-dot { animation-play-state: paused; }
@media (prefers-reduced-motion: reduce) {
  .ec-live-road, .ec-live-dot { animation: none; }
}
`;

export default function LiveTruckIndicator({
  size = 28,
  speed = 0.7,
  paused = false,
  color = "#2FBF6B",
  className,
  ariaHidden = false,
  label = "Live",
}: LiveTruckIndicatorProps) {
  const iconH = Math.round(size * 0.72);
  const iconW = Math.round(iconH * (34 / 24));

  return (
    <span
      className={`${paused ? "ec-live-paused " : ""}${className ?? ""}`}
      role={ariaHidden ? undefined : "img"}
      aria-label={ariaHidden ? undefined : `${label} tracking active`}
      aria-hidden={ariaHidden ? true : undefined}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: Math.round(size * 0.18),
        height: size,
        boxSizing: "border-box",
        verticalAlign: "middle",
        color,
        lineHeight: 1,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      <style>{CSS}</style>

      <svg width={iconW} height={iconH} viewBox="0 0 34 24" fill="none" aria-hidden="true" style={{ display: "block", flexShrink: 0 }}>
        {/* Truck: cargo box + cab, outlined so it stays crisp when small */}
        <g stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" fill="none">
          <rect x="1" y="3" width="17" height="11" />
          <path d="M18 6h6l4 4v4h-10z" />
          <circle cx="7" cy="17" r="2.2" />
          <circle cx="24" cy="17" r="2.2" />
        </g>
        {/* Road — the only moving part */}
        <line
          className="ec-live-road"
          x1="-6"
          y1="22"
          x2="40"
          y2="22"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="7 7"
          opacity="0.85"
          style={{ ["--ec-live-speed" as string]: `${speed}s` }}
        />
      </svg>

      <span
        style={{
          fontSize: Math.max(10, Math.round(size * 0.38)),
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          lineHeight: 1,
          display: "block",
        }}
      >
        {label}
      </span>

      <svg
        width={Math.round(size * 0.28)}
        height={Math.round(size * 0.28)}
        viewBox="0 0 8 8"
        aria-hidden="true"
        style={{ display: "block", flexShrink: 0 }}
      >
        <circle className="ec-live-dot" cx="4" cy="4" r="3" fill={color} />
      </svg>
    </span>
  );
}
